import React from 'react'; //eslint-disable-line
import PropTypes from 'prop-types';
import { useSegment, useTrackingPage } from '../../../components/tracking/trackingProvider';

import useAppContext from '../../../context/useAppContext';
import AttendeeModel from '../../../models/attendeeModel';
import { useParams, useHistory, Link } from 'react-router-dom';
import i18n from 'i18next';

import { Icon, WeezHeader, Numbers, SplitContainer, AnchorNav, Card, HeaderCard, Button, ButtonDropdown } from '@weezevent/nacre';
import { AttendeeStatus } from '../list/components/attendee-row/attendee-row';
import { useTranslation, Trans } from 'react-i18next';
import ListItem from './components/list-item/listItem';
import Loading from '../../../components/loading';
import SeatingCard from './components/seating-card/seatingCard';
import TransactionDetails from './components/transaction-details/transactionDetails';
import RefundDetails from './components/refund/refundDetails';
import { Comment } from './components/comments/comments';
import { ModerationComment } from './components/comments/moderationcomment';
import SwapHistory from './components/swap-history/swapHistory';
import ChartedEvent from '../../../components/chartedEvent';
import FilledForm from './components/filled-form/filledForm';
import { initAction } from '../../order/list/list';
import ActionContainer from '../../order/action-container/actionContainer';
import { ListDropdownItems, spec } from '../list/list';
import TicketDetail from './components/ticket-detail/ticketDetail';
import { ResaleTicketCard } from './components/resaleticket-card/resaleTicketCard';
import SwapSession from './components/swap-session/swapSession';
import RefundStyle from './components/refund/refundDetails.module.css';

import Style from './detail.module.css';
import SwitchItem from './components/switch-item/SwitchItem';

function LabelHeader({ attendee }) {
    const { t } = useTranslation();

    return (
        <div className={Style['header-label-container']}>
            <Icon className={Style['header-icon-attendee']} name="user" />
            <div className={Style['header-label-attendee']}>
                <span className={Style['header-label']}>{` ${t('ticket.common.attendee')} : `}</span>
                <span>{attendee?.resolved_name}</span>
            </div>
            <AttendeeStatus className={Style['header-status']} attendee={attendee} />
        </div>
    );
}

LabelHeader.propTypes = {
    attendee: PropTypes.object.isRequired
};

function PriceDetailsCard({ attendee, event, locale, id }) {
    const { t } = useTranslation();

    return (
        <TransactionDetails id={id} title={t('ticket.manager.attendees.detail.priceDetail')}>
            <TransactionDetails.Item
                title={+attendee.fees_amount_ttc > 0 ? t('ticket.manager.attendees.detail.salesPriceVatFeesExcl') : t('ticket.manager.attendees.detail.salesPriceVat')}
            >
                <Numbers value={attendee.price_ticket} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
            </TransactionDetails.Item>
            {+attendee.fees_amount_ttc > 0 && (
                <TransactionDetails.Item title={t('ticket.manager.totalFees')}>
                    <Numbers value={attendee.fees_amount_ttc} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
                </TransactionDetails.Item>
            )}
            {+attendee.reduction_amount > 0 && (
                <TransactionDetails.Item title={t('ticket.manager.attendees.detail.reduction')}>
                    <Numbers value={-attendee.reduction_amount} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
                </TransactionDetails.Item>
            )}
            <TransactionDetails.Item title={t('ticket.manager.totalPrice')} blue>
                <Numbers value={attendee.paid_price} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
            </TransactionDetails.Item>
        </TransactionDetails>
    );
}

PriceDetailsCard.propTypes = {
    attendee: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    locale: PropTypes.string.isRequired
};

function ItemsDetailsCard({ attendee, event, locale, id }) {
    const { t } = useTranslation();

    let selectIcon = key => {
        switch (key) {
            case 1:
                return 'credit-card';
            case 2:
                return 'shirt';
            case 3:
                return 'service';

            default:
                break;
        }
    };

    const crossSellings = React.useMemo(() => {
        let _crossSellings = [];
        if (attendee.cross_sellings && attendee.cross_sellings.length > 0) {
            _crossSellings = attendee.cross_sellings.reduce((acc, item) => {
                let finded = acc.findIndex(e => e.variant.id === item.variant.id);

                if (finded !== -1) {
                    acc[finded].count += 1;
                } else {
                    acc.push({ ...item, count: 1 });
                }
                return acc;
            }, []);
        }
        return _crossSellings;
    }, [attendee, attendee.cross_sellings]);

    return (
        <TransactionDetails id={id} title={t('ticket.common.items')}>
            {crossSellings
                .filter(e => e && e.variant && e.variant.product)
                .map(e => {
                    return (
                        <TransactionDetails.ItemAction
                            key={e.id}
                            quantity={e.count > 1 ? e.count : undefined}
                            title={e.variant.product.name + ' - ' + e.variant.name}
                            number={e.barcode}
                            icone={selectIcon(e.variant.product.category)}
                        >
                            <Numbers value={e.paid_price} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
                        </TransactionDetails.ItemAction>
                    );
                })}

            <TransactionDetails.Item title={t('ticket.manager.totalPrice')} blue>
                <Numbers
                    value={crossSellings.reduce((previousValue, currentValue) => previousValue + parseInt(currentValue.paid_price, 10), 0)}
                    locale={locale}
                    currency={event?.currency || 'EUR'}
                    showNullDecimal
                />
            </TransactionDetails.Item>
        </TransactionDetails>
    );
}

ItemsDetailsCard.propTypes = {
    attendee: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    locale: PropTypes.string.isRequired
};

function RefundContent({ refund, currencyFormatter, type = 'create' }) {
    const { t } = useTranslation();
    let key = type === 'remove' ? 'ticket.manager.attendee.refund_detail_deleted' : 'ticket.manager.attendee.refund_detail';
    let origin_translations = React.useMemo(() => {
        if (refund.origin !== null) {
            return t('ticket.manager.attendee.refund_origin_widget', { origin: refund.origin });
        }
        if (refund.account) {
            return t('ticket.manager.attendee.refund_origin_bo', {
                first_name: refund.account.first_name,
                last_name: refund.account.last_name,
                email: refund.account.email
            });
        }

        return '';
    }, [refund]);

    return (
        <Trans
            i18nKey={key}
            values={{
                amount: currencyFormatter.format(refund.amount),
                fees_amount: refund.fees_amount,
                bluetext_className: RefundStyle['blue-text'],
                origin: origin_translations
            }}
            components={{
                span: <span />
            }}
        />
    );
}

function HistoryCard({ id, attendee, event, locale }) {
    const { t } = useTranslation();
    if (!attendee.refund_requests?.length) {
        return null;
    }

    return (
        <Card id={id}>
            <HeaderCard>
                <Card.Title>{t('ticket.manager.attendee.detail.history_title')}</Card.Title>
            </HeaderCard>
            <div className={Style['padding-15']}>
                <RefundDetails locale={locale} currency={event.currency} refundRequests={attendee?.refund_requests} refundContent={RefundContent} />
            </div>
        </Card>
    );
}

HistoryCard.propTypes = {
    attendee: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    locale: PropTypes.string.isRequired
};

export function AttendeeDetail() {
    const { t } = useTranslation();
    const { track } = useSegment();

    const { attendee_id } = useParams();
    const [{ event, organization }] = useAppContext();
    const history = useHistory();
    const locale = i18n.language;

    const [action, setAction] = React.useState(initAction);

    const prefix = React.useMemo(() => {
        return `/ticket/O${organization.id}/E${event.id}/manager`;
    }, [event, organization]);

    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const [attendee, loading, sync] = AttendeeModel.useApiModel(
        {
            //eslint-disable-line
            id: attendee_id,
            eventId: event.pk,
            organizationId: organization.pk,
            query: {
                sync: true,
                include_deleted_fields: true,
                include_default_fields: true,
                include: ['rate.form', 'form.field', 'cross_sellings', 'resold_to', 'resold_from'],
                expand: ['rate.group', 'order.discount', 'refund_requests', 'cross_sellings', 'cross_sellings.variant.product', 'resold_to', 'resold_from']
            }
        },
        [attendee_id]
    );

    useTrackingPage('VIEW - ATTENDEE PROFILE', { 'Page Title': 'ATTENDEE PROFILE' });

    const addNavTracking = (translation, element) => {
        return <div onClick={() => track('ACTION > ANCHOR NAV', { 'Page Title': 'ATTENDEE PROFILE', 'Anchor element': element })}>{translation}</div>;
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <>
            <WeezHeader
                title={<LabelHeader attendee={attendee} />}
                backLink={{
                    onClick: () => {
                        history.push(`${prefix}/attendees`);
                        track('ACTION > BACK', { 'Page Title': 'ATTENDEE PROFILE' });
                    }
                }}
                rightComponent={[
                    <div className={Style['switch-attendee-container']} key="right-block">
                        <SwitchItem
                            itemId={attendee?.pk}
                            prefix={prefix}
                            spec={spec}
                            model={AttendeeModel}
                            queryParams={{
                                include_deleted: true,
                                include_deleted_fields: true,
                                sync: false,
                                should_not_count: true
                            }}
                        />
                        <ButtonDropdown
                            renderWhenClosed
                            trigger={
                                <Button
                                    onClick={() => track('ACTION > ACTION ORDER DETAILS', { 'Page Title': 'ORDER DETAILS' })}
                                    inverted
                                    primary
                                    icon={<Icon name="chevron-bottom" />}
                                    label={t('ticket.common.cta.actions')}
                                />
                            }
                            items={[<ListDropdownItems key="list" attendee={attendee} onChangeAction={setAction} prefix={prefix} history={history} mode={'detail'} />]}
                        />
                    </div>
                ]}
            >
                <Link
                    to={`${prefix}/attendees`}
                    className={Style['header-link-manager']}
                    onClick={() => track('ACTION > CLICK ATTENDEE BREADCRUMB', { 'Page Title': 'ATTENDEE PROFILE' })}
                >
                    {t('ticket.manager.attendees.attendeeManager')}
                </Link>
                {` > ${attendee?.id_weez_ticket}`}
            </WeezHeader>
            <AnchorNav
                items={[
                    {
                        id: 'ticket',
                        title: addNavTracking(t('ticket.common.ticket'), 'Ticket')
                    },
                    {
                        id: 'details',
                        title: addNavTracking(t('ticket.manager.attendees.detail.priceDetail'), 'Price')
                    },
                    ...(attendee?.cross_sellings?.length > 0
                        ? [
                              {
                                  id: 'items',
                                  title: t('ticket.manager.attendees.detail.articles')
                              }
                          ]
                        : []),
                    ...(attendee?.refund_requests.length > 0
                        ? [
                              {
                                  id: 'refunds',
                                  title: t('ticket.manager.orders.detail.refundRequests')
                              }
                          ]
                        : []),

                    {
                        id: 'form',
                        title: addNavTracking('Informations', 'Participant')
                    }
                ]}
                rootId="main"
            >
                <SplitContainer
                    additional={
                        <>
                            <Card className={Style['list-orders']}>
                                <HeaderCard separator={false}>
                                    <div>{t('ticket.manager.attendees.ticketRelatedOrder')}</div>
                                </HeaderCard>
                                <ListItem
                                    onClick={() => track('ACTION > ORDER SWITCH', { 'Page Title': 'ATTENDEE PROFILE' })}
                                    subtitle={attendee.order.transaction_reference}
                                    url={`${prefix}/orders/${attendee.order.id_transaction}`}
                                    icon={<Icon name="package" />}
                                >
                                    {attendee.resolved_buyer_name}
                                </ListItem>
                            </Card>
                            {event.has_resale_config && (
                                <ResaleTicketCard resoldTo={attendee.resold_to} resoldFrom={attendee.resold_from} currency={event?.currency} prefix={prefix} />
                            )}
                            {(event.is_moderation_enabled || (!event.is_moderation_enabled && Boolean(attendee.comment))) && (
                                <ModerationComment
                                    attendeeId={attendee.pk}
                                    legacyComment={attendee.comment}
                                    attendee={attendee}
                                    sync={sync}
                                    event={event}
                                    isReadOnly={!event.is_moderation_enabled || attendee.removed}
                                />
                            )}
                            <Comment attendeeId={attendee.pk} />
                        </>
                    }
                >
                    <ActionContainer mode={'attendees'} sync={() => sync({ allow_cache: false, cache: false })} onClose={() => setAction(initAction)} {...action} />
                    <TicketDetail attendee={attendee} event={event} locale={locale} sync={() => sync({ allow_cache: false, cache: false })} />
                    <SwapSession attendee={attendee} onChangeAction={setAction} title={t('ticket.common.sessions')} />
                    <SeatingCard booking={attendee?.booking} title={t('ticket.common.seating.seating')} event={event} />
                    <PriceDetailsCard id="details" attendee={attendee} event={event} locale={locale} />
                    {attendee.cross_sellings.length > 0 && <ItemsDetailsCard id="items" attendee={attendee} event={event} locale={locale} />}
                    <FilledForm
                        id="form"
                        title={t('ticket.manager.attendees.detail.filledForm.title')}
                        subtitle={t('ticket.manager.attendees.detail.filledForm.subtitle')}
                        attendee={attendee}
                        event={event}
                        locale={locale}
                        onSubmit={sync}
                        onEdit={() => track('ACTION > EDIT PARTICIPANT', { 'Page Title': 'ATTENDEE PROFILE' })}
                    />
                    <HistoryCard id="refunds" attendee={attendee} event={event} locale={locale} />
                    {!attendee.removed && !event.use_sessions && (
                        <ChartedEvent event={event}>
                            <SwapHistory attendeeId={attendee.pk} />
                        </ChartedEvent>
                    )}
                </SplitContainer>
            </AnchorNav>
        </>
    );
}
